import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MEGA_GROUPS, TOTAL_SERVICE_COUNT } from "./navConfig";
import { CATEGORY_ICONS } from "./navIcons";
import { DropdownShell } from "./NavPrimitives";

const slugifyTitle = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-");

/** Category overview card — links to the first service of the category on its techies page. */
function CategoryCard({ group, cat, count, onNavigate }) {
  const Icon = CATEGORY_ICONS[cat.title];
  return (
    <Link
      to={group.anchor(cat.slugs[0])}
      onClick={onNavigate}
      data-testid={`${group.colTestIdPrefix}-card-${slugifyTitle(cat.title)}`}
      className="group flex items-start gap-3 border border-slate-200 hover:border-topo bg-paper-dark/20 hover:bg-white p-3 transition-colors"
    >
      <span className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-sm bg-topo text-white group-hover:bg-orange-survey transition-colors">
        {Icon && <Icon className="h-4 w-4" strokeWidth={1.75} />}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 display text-[13px] font-extrabold tracking-tight text-slate-900">
          {cat.title}
          <ArrowRight className="h-3 w-3 text-orange-survey -translate-x-0.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
        <span className="block text-[11px] text-slate-500 mt-0.5">{count} services · {cat.blurb}</span>
      </span>
    </Link>
  );
}

/** A single mega-menu group (header + 3 category columns). */
function MegaGroup({ group, onNavigate }) {
  const { icon: GroupIcon, title, landingTo, landingLabel, landingTestId, categories, services, colTestIdPrefix, linkTestIdPrefix, href, iconFor } = group;
  return (
    <>
      <div className="px-5 pt-4 pb-2 border-t first:border-t-0 border-b border-slate-100 bg-paper-dark/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GroupIcon className="h-4 w-4 text-topo" />
          <span className="display text-sm font-extrabold tracking-tight text-slate-900">{title}</span>
          <span className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500">· {services.length} services</span>
        </div>
        <Link to={landingTo} onClick={onNavigate} data-testid={landingTestId} className="mono text-[10px] tracking-[0.22em] uppercase text-topo hover:text-orange-survey transition-colors inline-flex items-center gap-1">
          {landingLabel} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-px bg-slate-100">
        {categories.map((cat) => {
          const items = cat.slugs.map((slug) => services.find((x) => x.slug === slug)).filter(Boolean);
          return (
            <div key={cat.title} data-testid={`${colTestIdPrefix}-${slugifyTitle(cat.title)}`} className="bg-white p-4">
              <CategoryCard group={group} cat={cat} count={items.length} onNavigate={onNavigate} />
              <ul className="mt-2 space-y-0.5">
                {items.map((s) => {
                  const Icon = iconFor(s);
                  return (
                    <li key={s.slug}>
                      <Link to={href(s.slug)} onClick={onNavigate} data-testid={`${linkTestIdPrefix}-${s.slug}`} className="group flex items-center gap-2 py-1 px-1 rounded-sm hover:bg-paper-dark/40 transition-colors">
                        <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-topo transition-colors" />
                        <span className="text-[13px] font-semibold text-slate-800 group-hover:text-topo transition-colors">{s.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Services mega panel — both groups with category overview cards. */
export default function MegaMenu({ onNavigate }) {
  return (
    <DropdownShell align="center" width="w-[1040px]" testid="services-mega-menu">
      {MEGA_GROUPS.map((g) => (
        <MegaGroup key={g.id} group={g} onNavigate={onNavigate} />
      ))}
      <div className="border-t border-slate-200 bg-paper-dark/30 px-5 py-3 flex items-center justify-between">
        <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
          {TOTAL_SERVICE_COUNT} service lines · USA · Canada · Australia
        </span>
        <Link to="/services" onClick={onNavigate} data-testid="mega-view-all-link" className="inline-flex items-center gap-1 text-sm font-semibold text-topo hover:text-orange-survey transition-colors">
          View all services <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </DropdownShell>
  );
}
